// Journey system server functions: journal, challenges, calendar,
// notifications, certificates, aggregate state for the Life Journey Map.
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

function publicClient() {
  const url = process.env.SUPABASE_URL!;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient<Database>(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

// ---------------- JOURNAL ----------------
export const listJournal = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { search?: string; gate?: number; tag?: string; pinned?: boolean; limit?: number } = {}) => d)
  .handler(async ({ data, context }) => {
    let q = context.supabase
      .from("journal_entries")
      .select("id, title, body, gate_n, activation_slug, song_slug, book_slug, mood, tags, is_pinned, created_at, updated_at")
      .eq("user_id", context.userId)
      .order("updated_at", { ascending: false })
      .limit(Math.min(data.limit ?? 100, 500));
    if (data.gate != null) q = q.eq("gate_n", data.gate);
    if (data.pinned) q = q.eq("is_pinned", true);
    if (data.tag) q = q.contains("tags", [data.tag]);
    if (data.search) q = q.or(`title.ilike.%${data.search}%,body.ilike.%${data.search}%`);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

export const saveJournal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: {
    id?: string;
    title?: string | null;
    body: string;
    gate_n?: number | null;
    activation_slug?: string | null;
    song_slug?: string | null;
    book_slug?: string | null;
    mood?: string | null;
    tags?: string[];
    is_pinned?: boolean;
  }) => d)
  .handler(async ({ data, context }) => {
    if (!data.body || data.body.length > 20000) throw new Error("Body required (max 20000 chars)");
    const payload = {
      user_id: context.userId,
      title: data.title?.slice(0, 300) ?? null,
      body: data.body,
      gate_n: data.gate_n ?? null,
      activation_slug: data.activation_slug?.slice(0, 200) ?? null,
      song_slug: data.song_slug?.slice(0, 200) ?? null,
      book_slug: data.book_slug?.slice(0, 200) ?? null,
      mood: data.mood?.slice(0, 50) ?? null,
      tags: (data.tags ?? []).slice(0, 20).map((t) => t.slice(0, 40)),
      is_pinned: !!data.is_pinned,
    };
    if (data.id) {
      const { data: row, error } = await context.supabase
        .from("journal_entries")
        .update(payload)
        .eq("id", data.id)
        .eq("user_id", context.userId)
        .select()
        .single();
      if (error) throw new Error(error.message);
      return row;
    }
    const { data: row, error } = await context.supabase
      .from("journal_entries")
      .insert(payload)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return row;
  });

export const deleteJournal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("journal_entries").delete().eq("id", data.id).eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const exportJournal = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("journal_entries")
      .select("*")
      .eq("user_id", context.userId)
      .order("created_at");
    if (error) throw new Error(error.message);
    return { exported_at: new Date().toISOString(), entries: data ?? [] };
  });

// ---------------- CHALLENGES ----------------
export const listChallengeTemplates = createServerFn({ method: "GET" }).handler(async () => {
  const supa = publicClient();
  const { data, error } = await supa
    .from("challenge_templates")
    .select("*")
    .eq("status", "published")
    .order("sort_order")
    .order("title");
  if (error) throw new Error(error.message);
  return data ?? [];
});

export const listMyChallenges = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("user_challenges")
      .select("id, template_id, started_on, completed_at, abandoned_at, created_at, updated_at, challenge_templates(slug, title, duration_days, cover_url)")
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    const ids = (data ?? []).map((r) => r.id);
    const progress = ids.length
      ? (await context.supabase
          .from("user_challenge_progress")
          .select("user_challenge_id, day_number, completed_at")
          .in("user_challenge_id", ids)).data ?? []
      : [];
    return { challenges: data ?? [], progress };
  });

export const enrollChallenge = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { template_id: string }) => d)
  .handler(async ({ data, context }) => {
    const today = new Date().toISOString().slice(0, 10);
    const { data: row, error } = await context.supabase
      .from("user_challenges")
      .upsert(
        { user_id: context.userId, template_id: data.template_id, started_on: today },
        { onConflict: "user_id,template_id,started_on" },
      )
      .select()
      .single();
    if (error) throw new Error(error.message);
    return row;
  });

export const checkInChallenge = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { user_challenge_id: string; day_number: number; note?: string }) => d)
  .handler(async ({ data, context }) => {
    const { data: row, error } = await context.supabase
      .from("user_challenge_progress")
      .upsert(
        {
          user_challenge_id: data.user_challenge_id,
          user_id: context.userId,
          day_number: data.day_number,
          note: data.note?.slice(0, 500) ?? null,
        },
        { onConflict: "user_challenge_id,day_number" },
      )
      .select()
      .single();
    if (error) throw new Error(error.message);
    // If completion count matches template duration, mark completed and issue certificate.
    // Certificate issuance runs through a SECURITY DEFINER function that re-verifies
    // completion server-side — clients cannot insert certificate rows directly.
    const { data: uc } = await context.supabase
      .from("user_challenges")
      .select("id, completed_at")
      .eq("id", data.user_challenge_id)
      .eq("user_id", context.userId)
      .maybeSingle();
    if (uc && !uc.completed_at) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { error: issueError } = await (supabaseAdmin as any).rpc(
        "issue_challenge_certificate_for",
        { _user: context.userId, _user_challenge_id: data.user_challenge_id },
      );
      if (!issueError) {
        await context.supabase.from("user_challenges").update({ completed_at: new Date().toISOString() }).eq("id", uc.id);
      }
    }

    return row;
  });

// ---------------- CALENDAR ----------------
export const listCalendar = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { from?: string; to?: string } = {}) => d)
  .handler(async ({ data, context }) => {
    let q = context.supabase
      .from("calendar_items")
      .select("*")
      .eq("user_id", context.userId)
      .order("starts_at");
    if (data.from) q = q.gte("starts_at", data.from);
    if (data.to) q = q.lte("starts_at", data.to);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

export const saveCalendarItem = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: {
    id?: string;
    title: string;
    notes?: string;
    category?: string;
    starts_at: string;
    ends_at?: string;
    all_day?: boolean;
    link?: string;
  }) => d)
  .handler(async ({ data, context }) => {
    const payload = {
      user_id: context.userId,
      title: data.title.slice(0, 200),
      notes: data.notes?.slice(0, 2000) ?? null,
      category: data.category ?? "personal",
      starts_at: data.starts_at,
      ends_at: data.ends_at ?? null,
      all_day: !!data.all_day,
      link: data.link?.slice(0, 500) ?? null,
    };
    if (data.id) {
      const { data: row, error } = await context.supabase
        .from("calendar_items")
        .update(payload)
        .eq("id", data.id)
        .eq("user_id", context.userId)
        .select()
        .single();
      if (error) throw new Error(error.message);
      return row;
    }
    const { data: row, error } = await context.supabase.from("calendar_items").insert(payload).select().single();
    if (error) throw new Error(error.message);
    return row;
  });

export const deleteCalendarItem = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("calendar_items").delete().eq("id", data.id).eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------------- NOTIFICATIONS ----------------
export const listNotifications = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { category?: string; limit?: number } = {}) => d)
  .handler(async ({ data, context }) => {
    let q = context.supabase
      .from("notifications")
      .select("*")
      .eq("user_id", context.userId)
      .is("dismissed_at", null)
      .order("created_at", { ascending: false })
      .limit(Math.min(data.limit ?? 100, 300));
    if (data.category) q = q.eq("category", data.category);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

export const markNotificationsRead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { ids?: string[]; all?: boolean } = {}) => d)
  .handler(async ({ data, context }) => {
    const now = new Date().toISOString();
    let q = context.supabase.from("notifications").update({ read_at: now }).eq("user_id", context.userId).is("read_at", null);
    if (!data.all && data.ids?.length) q = q.in("id", data.ids);
    const { error } = await q;
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const dismissNotification = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("notifications")
      .update({ dismissed_at: new Date().toISOString() })
      .eq("id", data.id)
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------------- PROFILE ----------------
export const getMyProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase.from("profiles").select("*").eq("id", context.userId).maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  });

export const updateMyProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: {
    display_name?: string;
    bio?: string;
    avatar_url?: string;
    current_gate?: number | null;
    learning_path?: string | null;
    visibility?: "private" | "members" | "public";
    notification_prefs?: Record<string, boolean>;
    onboarding_path?: string;
    mark_onboarded?: boolean;
  }) => d)
  .handler(async ({ data, context }) => {
    const patch: Database["public"]["Tables"]["profiles"]["Update"] = {};
    if (data.display_name != null) patch.display_name = data.display_name.slice(0, 100);
    if (data.bio != null) patch.bio = data.bio.slice(0, 2000);
    if (data.avatar_url != null) patch.avatar_url = data.avatar_url.slice(0, 500);
    if (data.current_gate !== undefined) patch.current_gate = data.current_gate;
    if (data.learning_path !== undefined) patch.learning_path = data.learning_path;
    if (data.visibility) patch.visibility = data.visibility;
    if (data.notification_prefs) patch.notification_prefs = data.notification_prefs;
    if (data.onboarding_path) patch.onboarding_path = data.onboarding_path.slice(0, 40);
    if (data.mark_onboarded) patch.onboarded_at = new Date().toISOString();
    const { data: row, error } = await context.supabase
      .from("profiles")
      .update(patch)
      .eq("id", context.userId)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return row;
  });

// ---------------- CERTIFICATES ----------------
export const listMyCertificates = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("certificates")
      .select("*")
      .eq("user_id", context.userId)
      .order("issued_on", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

// Testable core: resolves certificate metadata via the admin client (the
// verify_certificate RPC is no longer callable by anon/authenticated).
// Exported so integration tests can mock the admin client and cover success
// and failure paths without going through the createServerFn RPC boundary.
export async function verifyCertificateImpl(code: string) {
  if (!code || code.length > 100) return null;
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data: rows, error } = await supabaseAdmin
    .from("certificates")
    .select("verify_code, program_kind, program_ref, program_title, recipient_name, issued_on")
    .eq("verify_code", code)
    .limit(1);
  if (error) throw new Error(error.message);
  return rows?.[0] ?? null;
}

export const verifyCertificate = createServerFn({ method: "GET" })
  .inputValidator((d: { code: string }) => d)
  .handler(async ({ data }) => verifyCertificateImpl(data.code));


// ---------------- JOURNEY STATE (map aggregate) ----------------
export const getJourneyState = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const uid = context.userId;
    const [profile, completions, favorites, reading, badges, certs, chals] = await Promise.all([
      context.supabase.from("profiles").select("*").eq("id", uid).maybeSingle(),
      context.supabase.from("activation_completions").select("activation_slug, completed_at").eq("user_id", uid),
      context.supabase.from("user_favorites").select("kind, target_slug, created_at").eq("user_id", uid),
      context.supabase.from("reading_progress").select("title_id, percent, last_read_at").eq("user_id", uid),
      context.supabase.from("user_badges").select("badge_id, awarded_at").eq("user_id", uid),
      context.supabase.from("certificates").select("id, program_kind, program_ref, issued_on").eq("user_id", uid),
      context.supabase
        .from("user_challenges")
        .select("id, template_id, started_on, completed_at, abandoned_at, challenge_templates(slug, title, duration_days)")
        .eq("user_id", uid),
    ]);
    return {
      profile: profile.data,
      completions: completions.data ?? [],
      favorites: favorites.data ?? [],
      reading: reading.data ?? [],
      badges: badges.data ?? [],
      certificates: certs.data ?? [],
      challenges: chals.data ?? [],
    };
  });

// ---------------- FEEDBACK ----------------
export const submitFeedback = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { kind: string; subject: string; message: string; page_path?: string }) => d)
  .handler(async ({ data, context }) => {
    if (!data.subject || !data.message) throw new Error("Subject and message required");
    const { data: row, error } = await context.supabase
      .from("feedback_items")
      .insert({
        user_id: context.userId,
        kind: data.kind.slice(0, 40),
        subject: data.subject.slice(0, 200),
        message: data.message.slice(0, 4000),
        page_path: data.page_path?.slice(0, 300) ?? null,
      })
      .select()
      .single();
    if (error) throw new Error(error.message);
    return row;
  });

export const listFeedbackAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    // has_role check ensures RLS returns nothing for non-admins; guard explicitly too.
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin) throw new Error("Forbidden");
    const { data, error } = await context.supabase
      .from("feedback_items")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const updateFeedbackAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string; status?: string; admin_note?: string }) => d)
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    if (!isAdmin) throw new Error("Forbidden");
    const patch: Database["public"]["Tables"]["feedback_items"]["Update"] = {};
    if (data.status) patch.status = data.status.slice(0, 40);
    if (data.admin_note !== undefined) patch.admin_note = data.admin_note.slice(0, 2000);
    const { error } = await context.supabase.from("feedback_items").update(patch).eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
